import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MapPinIcon, ShieldCheckIcon, SparklesIcon, CheckCircle2Icon, MessageSquareIcon, PhoneIcon, HeartIcon, Share2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ListingCardProps } from './ListingCard';
interface ListingDetailModalProps {
  listing: ListingCardProps;
  open: boolean;
  onClose: () => void;
}
export function ListingDetailModal({
  listing,
  open,
  onClose
}: ListingDetailModalProps) {
  const [activeImage, setActiveImage] = useState(0);
  const [saved, setSaved] = useState(listing.isSaved ?? false);
  const aiSummary = ['Fair price based on current market comparison', 'GCC specifications confirmed', 'Mileage is reasonable for the model year', 'Seller response rate is high'];
  const prev = () => setActiveImage(i => (i - 1 + listing.images.length) % listing.images.length);
  const next = () => setActiveImage(i => (i + 1) % listing.images.length);
  return createPortal(<AnimatePresence data-id="element-2713" data-mp="31szyw7jqjgpw">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-8" data-id="element-2714" data-mp="31rybr4pg33rt">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2715" data-mp="2e2dq2e1wpseu" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[88vh] overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-label={listing.title} data-id="element-2716" data-mp="2o1cypwmo9o1u">
            {/* Close */}
            <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-20 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-2717" data-mp="1x4xfa7lt2dvr">
              <XIcon className="w-5 h-5 text-slate-700" data-id="element-2718" data-mp="1jbz4pe0skf7m" />
            </button>

            <div className="overflow-y-auto" data-id="element-2719" data-mp="1eccqp2d52uxr">
              {/* Gallery */}
              <div className="relative aspect-[16/9] bg-slate-100" data-id="element-2720" data-mp="1eefrmhfty8h9">
                <img src={listing.images[activeImage]} alt={listing.title} className="w-full h-full object-cover" data-id="element-2721" data-mp="2up9vtubjjxzt" />
                {listing.images.length > 1 && <>
                    <button onClick={prev} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-2722" data-mp="329uc3w8shkk7">
                      <ChevronLeftIcon className="w-5 h-5 text-slate-700" data-id="element-2723" data-mp="3g1s9j9t51s0j" />
                    </button>
                    <button onClick={next} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-2724" data-mp="3l14yq17hfb6a">
                      <ChevronRightIcon className="w-5 h-5 text-slate-700" data-id="element-2725" data-mp="2ugvkgvzsy39" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-2726" data-mp="7m8jskroerq8">
                      {listing.images.map((_, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`h-1.5 rounded-full transition-all ${idx === activeImage ? 'bg-white w-4' : 'bg-white/50 w-1.5'}`} data-id="element-2727" data-mp="2g0ovv2v0w0cl" data-mpi={idx} />)}
                    </div>
                  </>}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm" data-id="element-2728" data-mp="2ysrfmn2ynwr2">
                  <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-2729" data-mp="2p37vzf7fbyv5" />
                  <span className="text-sm font-bold bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-2730" data-mp="2p1d8pu8ojunw">
                    {listing.matchScore}% Match
                  </span>
                </div>
              </div>

              <div className="p-6 lg:p-8 space-y-6" data-id="element-2731" data-mp="3cssfbz7hoy4y">
                {/* Title + Price */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-2732" data-mp="zaxqdq2ii1fa">
                  <div data-id="element-2733" data-mp="148zb0ffl68sv">
                    <h2 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-2734" data-mp="14aacu9ph16hc">
                      {listing.title}
                    </h2>
                    <div className="flex items-center gap-1.5 text-slate-500" data-id="element-2735" data-mp="1da5vezlcw3re">
                      <MapPinIcon className="w-4 h-4" data-id="element-2736" data-mp="1d82uvlmkus8f" />
                      <span data-id="element-2737" data-mp="1i87dpiayjz1t">{listing.location}</span>
                    </div>
                  </div>
                  <span className="font-bold text-3xl text-primary-indigo whitespace-nowrap" data-id="element-2738" data-mp="1n7blq5m5bqqk">
                    {listing.price}
                  </span>
                </div>

                {/* AI Summary */}
                <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-2xl p-5 border border-primary-indigo/10" data-id="element-2739" data-mp="1vzdle1oe52zs">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2740" data-mp="25yl5d7pmrvvv">
                    <div className="p-2 bg-gradient-to-br from-primary-indigo to-ai-purple rounded-xl" data-id="element-2741" data-mp="25wqmdyxx6duf">
                      <SparklesIcon className="w-4 h-4 text-white" data-id="element-2742" data-mp="5u8weajkepw2" />
                    </div>
                    <h3 className="font-display font-bold text-dark-navy" data-id="element-2743" data-mp="vfjaj86x0wf">
                      AI Analysis
                    </h3>
                  </div>
                  <ul className="space-y-2.5" data-id="element-2744" data-mp="aun5zx0te7w7">
                    {aiSummary.map((item, idx) => <li key={idx} className="flex items-start gap-2.5 text-slate-700 text-sm" data-id="element-2745" data-mp="om1s4cq0qp7h" data-mpi={idx}>
                        <CheckCircle2Icon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-2746" data-mp="yl0nwupoqqd2" />
                        <span data-id="element-2747" data-mp="tmfogxi9capo">{item}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Key info grid */}
                <div data-id="element-2748" data-mp="30eq9zupl74l4">
                  <h3 className="font-display font-bold text-lg text-dark-navy mb-4" data-id="element-2749" data-mp="2vdsphbh3tygg">
                    Key Information
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-2750" data-mp="1awteduw382bo">
                    {listing.specs.map((spec, idx) => <div key={idx} className="bg-slate-50 rounded-xl px-4 py-3" data-id="element-2751" data-mp="1fwfq1zw9paid" data-mpi={idx}>
                        <span className="block text-xs text-slate-500 mb-1" data-id="element-2752" data-mp="163qi3ezxeers">
                          {spec.label}
                        </span>
                        <span className="font-medium text-slate-900" data-id="element-2753" data-mp="s7d8st5jpjoe">
                          {spec.value}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Seller */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200" data-id="element-2754" data-mp="n6fs6l06oxp0">
                  <div className="flex items-center gap-4" data-id="element-2755" data-mp="2clx0es673kqe">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-lg font-bold text-slate-400" data-id="element-2756" data-mp="2hjou9rqehgx8">
                      {listing.title.charAt(0)}
                    </div>
                    <div data-id="element-2757" data-mp="10az01tztene7">
                      <h4 className="font-bold text-dark-navy" data-id="element-2758" data-mp="10ctkz8b36fia">
                        {listing.sellerType === 'Verified Business' ? 'Verified Dealer' : listing.sellerType}
                      </h4>
                      <div className="flex items-center gap-1.5 text-sm mt-0.5" data-id="element-2759" data-mp="lmmc9kavsaus">
                        {listing.sellerType === 'Verified Business' ? <>
                            <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-2760" data-mp="hndgyb0704x4" />
                            <span className="text-success font-medium" data-id="element-2761" data-mp="cm69ye5vj9ri">
                              Trusted seller · 99% response rate
                            </span>
                          </> : <span className="text-slate-500" data-id="element-2762" data-mp="220uksaytgjnf">
                            Private seller · usually replies within hours
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Actions */}
            <div className="border-t border-slate-100 p-4 lg:px-8 bg-white flex flex-wrap items-center gap-3" data-id="element-2763" data-mp="2jaoovgjvl2q4">
              <button className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-2764" data-mp="2obcgo4fxhnle">
                <MessageSquareIcon className="w-5 h-5" data-id="element-2765" data-mp="1vmoqbugbo80z" />
                Message Seller
              </button>
              <button className="flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#20BD5A] transition-colors" data-id="element-2766" data-mp="20m1o3a8qezic">
                <PhoneIcon className="w-5 h-5" data-id="element-2767" data-mp="3byc8otdbnpbp" />
                WhatsApp
              </button>
              <button onClick={() => setSaved(s => !s)} className="flex items-center justify-center gap-2 py-3 px-4 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors" data-id="element-2768" data-mp="3gwm7ddy99822">
                <HeartIcon className={`w-5 h-5 ${saved ? 'fill-error text-error' : ''}`} data-id="element-2769" data-mp="3se185guabw53" />
                {saved ? 'Saved' : 'Save'}
              </button>
              <button className="flex items-center justify-center gap-2 py-3 px-4 bg-slate-50 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors" data-id="element-2770" data-mp="30mlc6w6u8x27">
                <Share2Icon className="w-5 h-5" data-id="element-2771" data-mp="34ogjchbga4nw" />
                Share
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}